"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";

import Image from "next/image";

import ChatIcon from '@mui/icons-material/Chat';
import FacebookIcon from '@mui/icons-material/Facebook';
import {
	Box,
	IconButton,
	InputAdornment,
	Menu,
	MenuItem,
	Stack,
	Typography
} from "@mui/material";

import { Field } from "@onair/minimal-ui/hook-form";
import { Iconify } from "@onair/minimal-ui/iconify";

import { SocialCommunityType } from "@onair/repositories";

import InputLabelWizard from "./input-label-wizard";

const getTypeIcon = (socialCommunityType: SocialCommunityType) => {
	switch (socialCommunityType) {
		case SocialCommunityType.ZALO:
			return <Image src="/assets/icons/social/zalo.png" alt="Zalo" width={24} height={24} unoptimized />;
		case SocialCommunityType.FACEBOOK:
			return <FacebookIcon fontSize="medium" />;
		default:
			return <ChatIcon fontSize="medium" />;
	}
};

interface SocialCommunityFieldProps {
	index: number;
	removeField: () => void;
	fieldName: string;
}

const SocialCommunityField: React.FC<SocialCommunityFieldProps> = ({
	index,
	removeField,
	fieldName
}) => {
	const { control, setValue } = useFormContext();
	const socialCommunityType = useWatch({
		name: `${fieldName}.socialCommunityType`,
	});

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const handleIconClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleTypeSelect = (type: SocialCommunityType) => {
		setValue(`${fieldName}.socialCommunityType`, type);
		handleMenuClose();
	};

	const memoizedTypeIcon = useMemo(() => getTypeIcon(socialCommunityType), [socialCommunityType]);

	return (
		<Box sx={{ position: 'relative' }}>
			<Stack spacing={2}>
				<Field.Text
					name={`${fieldName}.socialCommunityName`}
					label="Tên nhóm"
					fullWidth
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<IconButton
									edge="start"
									size="medium"
									onClick={handleIconClick}
									sx={{ mr: 0.5 }}
									aria-label="select social community type"
									aria-controls={open ? `social-community-type-menu-${index}` : undefined}
									aria-haspopup="true"
									aria-expanded={open ? "true" : undefined}
								>
									{memoizedTypeIcon}
								</IconButton>
								<Menu
									id={`social-community-type-menu-${index}`}
									anchorEl={anchorEl}
									open={open}
									onClose={handleMenuClose}
									MenuListProps={{
										'aria-labelledby': 'social-community-type-button',
									}}
								>
									<MenuItem onClick={() => handleTypeSelect(SocialCommunityType.ZALO)}>
										<Stack direction="row" alignItems="center" spacing={1}>
											<Image src="/assets/icons/social/zalo.png" alt="Zalo" width={24} height={24} unoptimized />
											<Typography variant="body2" className="font-semibold">Zalo</Typography>
										</Stack>
									</MenuItem>
								</Menu>
							</InputAdornment>
						)
					}}
				/>
				<Controller
					control={control}
					name={`${fieldName}.socialCommunityType`}
					defaultValue={SocialCommunityType.ZALO}
					render={({ field }) => (
						<input type="hidden" {...field} />
					)}
				/>
				<Field.Text
					name={`${fieldName}.socialCommunityLink`}
					label="Link"
					fullWidth
					placeholder="https://"
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Iconify
									icon="solar:link-minimalistic-2-bold"
									className="text-primary w-6 h-6 font-bold"
								/>
							</InputAdornment>
						)
					}}
					helperText={
						socialCommunityType === SocialCommunityType.FACEBOOK
							? "Định dạng: https://www.facebook.com/groups/<GroupID>"
							: "Định dạng: https://zalo.me/g/<GroupID>"
					}
				/>
			</Stack>
		</Box>
	);
};

interface SocialCommunitySectionProps {
	prefixName?: string;
	title?: React.ReactNode;
}

const SocialCommunitySection: React.FC<SocialCommunitySectionProps> = ({
	prefixName = "",
	title = "Nhóm cộng đồng"
}) => {
	const { control } = useFormContext();

	const fieldArrayName = `${prefixName ? `${prefixName}.` : ''}socialCommunities`;

	const { fields, append, remove } = useFieldArray({
		control,
		name: fieldArrayName
	});

	useEffect(() => {
		if (fields.length === 0) {
			append({
				socialCommunityType: SocialCommunityType.ZALO,
				socialCommunityName: '',
				socialCommunityLink: ''
			});
		}
	}, [append, fields]);

	return (
		<Stack spacing={3}>
			{typeof title === 'string' ? <InputLabelWizard>{title}</InputLabelWizard> : title}

			<Stack spacing={4}>
				{fields.length > 0 && (
					<SocialCommunityField
						key={fields[0].id}
						index={0}
						fieldName={`${fieldArrayName}[0]`}
						removeField={() => { }}
					/>
				)}
			</Stack>
		</Stack>
	);
};

export default SocialCommunitySection;
